import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ErrorHandler } from '../utils/errorHandler';

const hasGoogleClientId = Boolean((process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim());

/**
 * Google Sign-In Button Component
 * Handles Google OAuth flow and integrates with AuthContext
 * Uses GoogleLogin component which provides credential with id_token.
 * When REACT_APP_GOOGLE_CLIENT_ID is not set (e.g. production build missing env), renders a fallback so the page does not throw.
 */
const GoogleSignInButton = ({ userType = 'runner', onSuccess, onError, className = '' }) => {
  const { loginWithGoogle } = useAuth();

  const handleSuccess = async (credentialResponse) => {
    try {
      const { credential } = credentialResponse;
      
      if (!credential) {
        throw new Error('No credential received from Google');
      }

      // Call the auth context method with the ID token
      await loginWithGoogle(credential, userType);
      
      if (onSuccess) {
        onSuccess();
      } else {
        toast.success('Successfully signed in with Google!');
      }
    } catch (error) {
      ErrorHandler.logError('GoogleSignInButton', error);
      if (onError) {
        onError(error);
      } else {
        toast.error('Google Sign-In failed. Please try again.');
      }
    }
  };

  const handleError = (error) => {
    ErrorHandler.logError('GoogleSignInButton', error);
    if (onError) {
      onError(error);
    } else {
      toast.error('Google Sign-In failed. Please try again.');
    }
  };

  if (!hasGoogleClientId) {
    return (
      <div className={className}>
        <div
          className="google-sign-in-unavailable"
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#5f6368',
            background: '#f1f3f4',
            borderRadius: '4px',
            textAlign: 'center',
          }}
        >
          Google Sign-In is not configured. Use email and password.
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={handleError}
        useOneTap={false}
        text="continue_with"
        shape="rectangular"
        theme="outline"
        size="large"
      />
    </div>
  );
};

export default GoogleSignInButton;
